import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import { useLocalStorage } from 'usehooks-ts';
import { ErrorLike } from '@apollo/client';
import {
  Task,
  TaskCompletionStatusEnum,
  TaskScopeNameEnum,
  useInterventionBulkCloseMutation,
  useTaskCompletionStatusUpdateMutation,
} from 'graphql-common';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import useSearchParam from '@lib/hooks/useSearchParam';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import toast from '@lib/components/Toast/Toast';
import { GetUrlParams } from '@lib/enums/urls';
import { TaskCounter } from '@lib/interfaces/task';
import useTabParam from '@lib/hooks/useTabParam';
import usePrevious from '@lib/hooks/usePrevious';
import { Value, Values } from '@lib/interfaces/form';
import { DASHBOARD_OVERVIEW_FILTER_FORM } from '@lib/enums/localStorageKeys';
import { useWhoamiContext } from 'hooks/useWhoamiContext';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { getFormattedDate, transformDate } from '@lib/utils/dateTimeHelpers';
import { dateFormatForApi } from '@lib/enums/dateTime';
import { APP_URLS } from 'constants/urls';
import navigateToInterventionCreate from 'routes/Intervention/utils/navigateToInterventionCreate';
import getTaskCompletionSelectOption from 'routes/Intervention/utils/getTaskCompletionSelectOption';
import useDashboardOverviewTasksQueryHook from 'utils/fetch/hooks/useDashboardOverviewTasksQueryHook';
import dayjs, { Dayjs } from 'dayjs';
import { TASKS_OVERVIEW_FILTER_EMPTY_VALUES } from './useFilterFields';

const STATUSES_REQUIRING_INTERVENTION_CLOSE = [
  TaskCompletionStatusEnum.Solved,
  TaskCompletionStatusEnum.Unsolved,
];

export type UseActionsResult = {
  basePeriodEndParam: string;
  basePeriodStartParam: string;
  filterParams: Values;
  isPeriodCustomized: boolean;
  onCancelStatusChange: () => void;
  onConfirmStatusChange: () => void;
  onCreateIntervention: (item?: Task) => void;
  onResultStateChangeHandler: (
    task: Task,
    status: TaskCompletionStatusEnum,
  ) => void;
  onUpdateFilterParams: (values: Values) => void;
  onViewHandler: (task?: Task) => void;
  onViewIntervention: (item?: Task) => void;
  pendingStatusChange: { task: Task; status: TaskCompletionStatusEnum } | null;
  periodEndParam: string | null | undefined;
  periodStartParam: string | null | undefined;
  prevTabParam: TaskScopeNameEnum | string | undefined;
  resetAllFilterParams: () => void;
  resetFilterParams: () => void;
  resetPeriodFilterParams: () => void;
  setTabParam: (tab: TaskScopeNameEnum | string) => void;
  tabParam: TaskScopeNameEnum | string | undefined;
  taskCounterData: TaskCounter;
  taskResultStateUpdateError: unknown;
  taskResultStateUpdateLoading: boolean;
  tasks: Task[];
  tasksError?: ErrorLike;
  tasksFirstLoading: boolean;
  tasksLoading: boolean;
  totalTasksCount: number;
  defaultMinDateObj?: Dayjs;
  defaultMaxDateObj?: Dayjs;
};

function getDataFromFilterParams(
  filterParams: Values,
  defaultMinDate: string,
  defaultMaxDate: string,
) {
  const basePeriod = filterParams[TasksFilterFieldNames.Period] as string[];
  const basePeriodStartParam = basePeriod?.[0] || defaultMinDate;
  const basePeriodEndParam = basePeriod?.[1] || defaultMaxDate;
  const periodStartParam = getFormattedDate(
    basePeriodStartParam,
    dateFormatForApi,
  );
  const periodEndParam = getFormattedDate(basePeriodEndParam, dateFormatForApi);

  return {
    basePeriodEndParam,
    basePeriodStartParam,
    periodEndParam,
    periodStartParam,
  };
}

type Args = {
  withIntervention: boolean;
};

export default function useActions({
  withIntervention,
}: Args): UseActionsResult {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    task: Task;
    status: TaskCompletionStatusEnum;
  } | null>(null);
  const [searchQuery] = useSearchParam();
  const [filterParams, setFilterParams] = useLocalStorage<{
    [key: string]: Value;
  }>(DASHBOARD_OVERVIEW_FILTER_FORM, TASKS_OVERVIEW_FILTER_EMPTY_VALUES);
  const [paginationParams, setPaginationParams] = usePaginationParams();
  const [tabParam, setTabParam] = useTabParam({
    allowedTabs: Object.values(TaskScopeNameEnum),
    defaultTab: TaskScopeNameEnum.DashboardAll,
  });
  const prevTabParam = usePrevious(tabParam);

  // Whoami — reads ProtectedRoute's single query result. See useWhoamiContext.
  const { currentUser } = useWhoamiContext();

  // Data
  const defaultMinDate = _get(currentUser, 'company.createdAt') as string;
  const defaultMaxDate = useMemo(() => new Date().toISOString(), []);
  const defaultMinDateObj = defaultMinDate
    ? dayjs(defaultMinDate).startOf('day')
    : undefined;
  const defaultMaxDateObj = defaultMaxDate
    ? dayjs(defaultMaxDate).endOf('day')
    : undefined;
  const defaultMinDateStr = defaultMinDateObj
    ? transformDate(defaultMinDateObj.toDate())
    : undefined;
  const defaultMaxDateStr = defaultMaxDateObj
    ? transformDate(defaultMaxDateObj.toDate(), true)
    : undefined;
  const {
    basePeriodStartParam,
    basePeriodEndParam,
    periodStartParam,
    periodEndParam,
  } = getDataFromFilterParams(filterParams, defaultMinDate, defaultMaxDate);
  const isPeriodCustomized =
    Array.isArray(filterParams[TasksFilterFieldNames.Period]) &&
    !!filterParams[TasksFilterFieldNames.Period]?.[0];

  // Tasks
  const {
    refetchTasks,
    taskCounterData,
    tasks,
    tasksError,
    tasksFirstLoading,
    tasksLoading,
    totalTasksCount,
  } = useDashboardOverviewTasksQueryHook({
    searchQuery,
    filterParams,
    paginationParams,
    tabParam,
    defaultMinDate: defaultMinDateStr,
    defaultMaxDate: defaultMaxDateStr,
    withIntervention,
  });

  // Bulk Scheduling
  const [taskResultStateUpdateMutation, taskResultStateUpdateResult] =
    useTaskCompletionStatusUpdateMutation({
      onCompleted: ({ data: responseData }) => {
        if (responseData) {
          toast({ content: t('task-result-state-successfully-updated') });
        }
        refetchTasks();
      },
      onError: (error) => showGraphQLErrors(error, t),
    });
  const {
    error: taskResultStateUpdateError,
    loading: taskResultStateUpdateLoading,
  } = taskResultStateUpdateResult;

  const [interventionBulkCloseMutation] = useInterventionBulkCloseMutation({
    onError: (error) => showGraphQLErrors(error, t),
  });

  // Actions
  const onViewHandler = (task?: Task) => {
    if (task) {
      const { id } = task;
      navigate(
        APP_URLS.dashboard.tasks.reporting.getDynamicPath({
          pathParts: { id },
        }),
      );
    }
  };

  const onResultStateChangeHandler = (
    task: Task,
    status: TaskCompletionStatusEnum,
  ) => {
    if (
      task.intervention?.id &&
      !task.intervention.closedAt &&
      STATUSES_REQUIRING_INTERVENTION_CLOSE.includes(status)
    ) {
      setPendingStatusChange({ task, status });
    } else {
      taskResultStateUpdateMutation({
        variables: { taskId: task.id, attributes: { status } },
      });
    }
  };

  const onConfirmStatusChange = () => {
    if (!pendingStatusChange) return;
    const { task, status } = pendingStatusChange;
    const interventionId = task.intervention?.id;
    const taskId = task.id;
    setPendingStatusChange(null);
    if (interventionId) {
      interventionBulkCloseMutation({
        variables: { ids: [interventionId] },
        onCompleted: () => {
          taskResultStateUpdateMutation({
            variables: { taskId, attributes: { status } },
          });
        },
      });
    } else {
      taskResultStateUpdateMutation({
        variables: { taskId, attributes: { status } },
      });
    }
  };

  const onCancelStatusChange = () => setPendingStatusChange(null);

  const onCreateIntervention = (item?: Task) => {
    navigateToInterventionCreate(navigate, {
      assetId: item?.assetId,
      siteAreaId: item?.siteArea?.id,
      siteId: item?.site?.id,
      taskId: item?.id,
      // Each row is a task occurrence — link its own check, as the Task page
      // does, or the intervention leaves this occurrence Unsolved.
      taskCompletions: item?.completion
        ? [getTaskCompletionSelectOption(item.completion)]
        : undefined,
    });
  };

  const onViewIntervention = (item?: Task) => {
    const interventionId = _get(item, 'intervention.id');
    if (interventionId) {
      navigate(
        APP_URLS.dashboard.interventions.view.getDynamicPath({
          pathParts: { id: interventionId },
        }),
      );
    }
  };

  const resetFilterParams = useCallback(() => {
    setFilterParams({
      ...TASKS_OVERVIEW_FILTER_EMPTY_VALUES,
      ..._pick(filterParams, TasksFilterFieldNames.Period),
    });
    setPaginationParams({ ...paginationParams, [GetUrlParams.Page]: '1' });
  }, [filterParams, paginationParams, setFilterParams, setPaginationParams]);

  const resetPeriodFilterParams = useCallback(() => {
    setFilterParams({
      ...filterParams,
      [TasksFilterFieldNames.Period]: [defaultMinDateStr, defaultMaxDateStr],
    });
    setPaginationParams({ ...paginationParams, [GetUrlParams.Page]: '1' });
  }, [
    defaultMinDateStr,
    defaultMaxDateStr,
    filterParams,
    paginationParams,
    setFilterParams,
    setPaginationParams,
  ]);

  const resetAllFilterParams = useCallback(() => {
    setFilterParams(TASKS_OVERVIEW_FILTER_EMPTY_VALUES);
    setPaginationParams({ ...paginationParams, [GetUrlParams.Page]: '1' });
  }, [paginationParams, setFilterParams, setPaginationParams]);

  const onUpdateFilterParams = (values: Values) => {
    setFilterParams(values);
    setPaginationParams({ ...paginationParams, [GetUrlParams.Page]: '1' });
  };

  return {
    basePeriodEndParam,
    basePeriodStartParam,
    defaultMinDateObj,
    defaultMaxDateObj,
    filterParams,
    isPeriodCustomized,
    onCancelStatusChange,
    onConfirmStatusChange,
    onCreateIntervention,
    onResultStateChangeHandler,
    onUpdateFilterParams,
    onViewHandler,
    onViewIntervention,
    pendingStatusChange,
    periodEndParam,
    periodStartParam,
    prevTabParam,
    resetAllFilterParams,
    resetFilterParams,
    resetPeriodFilterParams,
    setTabParam,
    tabParam,
    taskCounterData,
    taskResultStateUpdateError,
    taskResultStateUpdateLoading,
    tasks,
    tasksError,
    tasksFirstLoading,
    tasksLoading,
    totalTasksCount,
  };
}
